// ─────────────────────────────────────────────────────────────────────────────
// TIENDA (Solar Hub) — REFERENCIAS, no bodega.
// No hay inventario que cargar. Cada categoría superior tiene subcategorías.
// El KIT no se vende aquí: se arma cuando el cotizador gratuito modela el techo.
// Precios = referencia. Stock = vacío hasta que un proveedor publique.
// ─────────────────────────────────────────────────────────────────────────────

import { DEFAULT_PANELS, DEFAULT_INVERTERS, DEFAULT_BATTERIES } from '../constants';
import { hubApiBaseUrl } from './hub';

export const CATEGORIAS = [
  {
    id: 'panel', label: 'Paneles', blurb: 'Módulos de referencia', img: '/tienda/cat-paneles.jpg',
    hijos: [
      { id: 'panel-mono', label: 'Monocristalino' },
      { id: 'panel-bifacial', label: 'Bifacial / N-Type' },
    ],
  },
  {
    id: 'inversor', label: 'Inversores', blurb: 'On-grid, híbrido, off-grid', img: '/tienda/cat-inversores.jpg',
    hijos: [
      { id: 'inv-ongrid-1f', label: 'On-grid 1F' },
      { id: 'inv-ongrid-3f', label: 'On-grid 3F' },
      { id: 'inv-hibrido', label: 'Híbridos' },
      { id: 'inv-offgrid', label: 'Off-grid / ZNI' },
    ],
  },
  {
    id: 'medidor', label: 'Medidores', blurb: 'Modbus · clase 1', img: '/tienda/prod-medidor.jpg',
    hijos: [
      { id: 'med-1f', label: 'Monofásicos' },
      { id: 'med-3f', label: 'Trifásicos' },
    ],
  },
  {
    id: 'bateria', label: 'Baterías', blurb: 'Litio, gel, BESS HV', img: '/tienda/cat-baterias.jpg',
    hijos: [
      { id: 'bat-litio', label: 'Litio 48 V' },
      { id: 'bat-gel', label: 'Gel 48 V' },
      { id: 'bat-hv', label: 'BESS HV' },
    ],
  },
  {
    id: 'modem', label: 'AMI / Gateways', blurb: 'Telemetría FluxAI', img: '/tienda/cat-ami.jpg',
    hijos: [{ id: 'ami-gw', label: 'Gateways' }],
  },
  {
    id: 'control', label: 'Control', blurb: 'FX-CTRL y mando', img: '/tienda/cat-ami.jpg',
    hijos: [{ id: 'ctrl-io', label: 'I/O' }],
  },
  {
    id: 'accesorio', label: 'Accesorios', blurb: 'CT, cable, corte', img: '/tienda/cat-estructura.jpg',
    hijos: [
      { id: 'acc-medida', label: 'Medida' },
      { id: 'acc-dc', label: 'DC / AC' },
    ],
  },
];

const SIN_STOCK = { stock: 'sin inventario', envio: 'cuando haya ficha de proveedor' };

function dePanel(p) {
  const bifacial = /bf|bifacial|n-type|nbf|de09/i.test(`${p.model} ${p.brand}`);
  return {
    sku: `ref-panel-${p.id}`, categoria: 'panel', sub: bifacial ? 'panel-bifacial' : 'panel-mono',
    origen: 'alebas', destacado: !!p.wp,
    nombre: `${p.brand} ${p.model}`, marca: p.brand, modelo: p.model,
    desc: `Módulo de referencia ${p.wp} W. No hay unidades en bodega: se cotiza al modelar.`,
    specs: [`${p.wp} W STC`, `Voc ${p.voc} V`, `Vmp ${p.vmp} V`, `${p.cellCount} celdas`, `${p.lengthMm}×${p.widthMm} mm`],
    ficha: { potencia: `${p.wp} W`, garantia: 'según fabricante', iva: true, ...SIN_STOCK },
    precioRef: p.price, img: '/tienda/prod-panel.jpg', referencia: true,
  };
}

function deInversor(p) {
  let sub = 'inv-ongrid-1f';
  if (p.type === 'hybrid') sub = 'inv-hibrido';
  else if (p.type === 'off-grid') sub = 'inv-offgrid';
  else if (p.phase === 3) sub = 'inv-ongrid-3f';
  const tipo = p.type === 'hybrid' ? 'híbrido' : p.type === 'off-grid' ? 'off-grid' : 'on-grid';
  return {
    sku: `ref-inv-${p.id}`, categoria: 'inversor', sub,
    origen: 'alebas', destacado: p.kw === 5 || p.kw === 10,
    nombre: `${p.brand} ${p.model}`, marca: p.brand, modelo: p.model,
    desc: `Inversor ${tipo} ${p.kw} kW ${p.phase}F. Referencia de modelado; no hay stock cargado.`,
    specs: [`${p.kw} kW`, `${p.phase}F`, `${p.mpptCount} MPPT`, `η ${p.efficiency} %`, tipo],
    ficha: { potencia: `${p.kw} kW`, garantia: 'según fabricante', iva: true, ...SIN_STOCK },
    precioRef: p.price, img: '/tienda/prod-inversor.jpg', referencia: true,
  };
}

function deBateria(p) {
  const fam = String(p.familia || p.chemistry || 'litio').toLowerCase();
  const sub = fam.includes('gel') ? 'bat-gel' : (fam.includes('hv') || fam.includes('bess') || (p.voltage || 0) >= 150) ? 'bat-hv' : 'bat-litio';
  return {
    sku: `ref-bat-${p.id}`, categoria: 'bateria', sub,
    origen: 'alebas', destacado: (p.kwh || 0) >= 5 && (p.kwh || 0) <= 10,
    nombre: `${p.brand} ${p.model}`, marca: p.brand, modelo: p.model,
    desc: `Banco ${p.kwh} kWh ${p.voltage} V (${p.chemistry || fam}). Referencia; sin inventario.`,
    specs: [`${p.kwh} kWh`, `${p.voltage} V`, p.chemistry || fam, p.cycles ? `${p.cycles} ciclos` : 'ciclos según ficha'],
    ficha: { potencia: `${p.kwh} kWh`, garantia: 'según fabricante', iva: true, ...SIN_STOCK },
    precioRef: p.price, img: '/tienda/prod-bateria.jpg', referencia: true,
  };
}

const MEDIDORES_REF = [
  { sku: 'sdm120', sub: 'med-1f', nombre: 'Eastron SDM120', modelo: 'SDM120-Modbus', fase: '1F', precio: 180000, specs: ['Monofásico 230 V', 'Modbus RTU', 'Clase 1', 'Riel DIN'] },
  { sku: 'sdm230', sub: 'med-1f', nombre: 'Eastron SDM230', modelo: 'SDM230-Modbus', fase: '1F', precio: 240000, specs: ['Monofásico 230 V', 'Modbus RTU', 'Bidireccional', 'Riel DIN'] },
  { sku: 'sdm630', sub: 'med-3f', nombre: 'Eastron SDM630', modelo: 'SDM630-Modbus', fase: '3F', precio: 420000, specs: ['Trifásico 3F+N', 'Modbus RTU', 'Hasta 100 A', 'Riel DIN'], destacado: true },
  { sku: 'sdm630mct', sub: 'med-3f', nombre: 'Eastron SDM630MCT', modelo: 'SDM630MCT', fase: '3F', precio: 480000, specs: ['Trifásico por CT /5 A', 'Modbus RTU', 'Indirecto', 'Riel DIN'] },
  { sku: 'sdm72d', sub: 'med-3f', nombre: 'Eastron SDM72D-M', modelo: 'SDM72D-M', fase: '3F', precio: 360000, specs: ['Trifásico compacto', 'Modbus RTU', 'Clase 1', 'Riel DIN'] },
];

const FIJOS_REF = [
  {
    sku: 'fx-en-gateway', categoria: 'modem', sub: 'ami-gw', origen: 'fluxai_hardware', destacado: true,
    nombre: 'Gateway FX-EN', marca: 'FluxAI', modelo: 'FX-EN',
    desc: 'Módem AMI. Referencia de la suite; el SaaS lo cobra FluxAI. Sin unidades cargadas.',
    specs: ['Ethernet / WiFi / 4G', 'RS485 Modbus', 'OTA', 'Corte remoto'],
    ficha: { garantia: '2 años', iva: true, ...SIN_STOCK },
    precioRef: 450000, img: '/tienda/prod-gateway.jpg', monitoreo: true, referencia: true,
  },
  {
    sku: 'fx-ctrl', categoria: 'control', sub: 'ctrl-io', origen: 'fluxai_hardware',
    nombre: 'Controlador FX-CTRL', marca: 'FluxAI', modelo: 'FX-CTRL',
    desc: 'DI/DO/AI para mando. Referencia; sin inventario.',
    specs: ['4 DI · 4 DO', '8 AI 4–20 mA', 'Modbus RTU', 'Riel DIN'],
    ficha: { garantia: '2 años', iva: true, ...SIN_STOCK },
    precioRef: 890000, img: '/tienda/cat-ami.jpg', referencia: true,
  },
  {
    sku: 'ct-100', categoria: 'accesorio', sub: 'acc-medida', origen: 'alebas',
    nombre: 'CT 100/5 núcleo partido', marca: 'Solar Hub', modelo: 'CT-100/5',
    desc: 'Transformador de corriente para SDM630MCT. Referencia.',
    specs: ['100/5 A', 'Núcleo partido', 'Clase 0.5'],
    ficha: { garantia: '1 año', iva: true, ...SIN_STOCK },
    precioRef: 35000, img: '/tienda/prod-medidor.jpg', referencia: true,
  },
  {
    sku: 'contactor-corte', categoria: 'accesorio', sub: 'acc-dc', origen: 'alebas',
    nombre: 'Contactor de corte 40 A', marca: 'Solar Hub', modelo: 'CT-40',
    desc: 'Corte remoto desde el gateway. Referencia.',
    specs: ['40 A', 'Bobina 230 VAC', 'Riel DIN'],
    ficha: { garantia: '1 año', iva: true, ...SIN_STOCK },
    precioRef: 120000, img: '/tienda/cat-estructura.jpg', referencia: true,
  },
];

export const PRODUCT_CATALOG = [
  ...DEFAULT_PANELS.map(dePanel),
  ...DEFAULT_INVERTERS.map(deInversor),
  ...DEFAULT_BATTERIES.map(deBateria),
  ...MEDIDORES_REF.map((m) => ({
    sku: m.sku, categoria: 'medidor', sub: m.sub, origen: 'alebas', destacado: !!m.destacado,
    nombre: m.nombre, marca: 'Eastron', modelo: m.modelo,
    desc: `Medidor ${m.fase} de referencia. Sin unidades en bodega.`,
    specs: m.specs, ficha: { garantia: '2 años', iva: true, ...SIN_STOCK },
    precioRef: m.precio, img: '/tienda/prod-medidor.jpg', referencia: true,
  })),
  ...FIJOS_REF,
];

let catalogoVivo = null;

export function setCatalogoVivo(rows) {
  catalogoVivo = Array.isArray(rows) && rows.length ? rows : null;
}

function origenTienda(eq) {
  const o = String(eq?.origen || 'alebas');
  if (o === 'proveedor' || o === 'proveedor_inscrito') return 'proveedor_inscrito';
  if (o === 'fluxai_hardware') return 'fluxai_hardware';
  return 'alebas';
}

/** Suma fichas del modelado al catálogo vivo. No inventa medidores ni SKUs locales. */
export function aplicarCatalogoFichas({ panels = [], inverters = [], batteries = [] } = {}) {
  const extra = [
    ...panels.map((p) => ({ ...dePanel(p), origen: origenTienda(p), sku: p.id || p.sku || `ref-panel-${p.brand}` })),
    ...inverters.map((p) => ({ ...deInversor(p), origen: origenTienda(p), sku: p.id || p.sku || `ref-inv-${p.brand}` })),
    ...batteries.map((p) => ({ ...deBateria(p), origen: origenTienda(p), sku: p.id || p.sku || `ref-bat-${p.brand}` })),
  ];
  const map = new Map((catalogoVivo || []).map((p) => [p.sku, p]));
  for (const r of extra) {
    if (!r.sku) continue;
    const prev = map.get(r.sku);
    map.set(r.sku, prev && prev.origen === 'alebas' ? { ...r, ...prev, origen: 'alebas' } : r);
  }
  const rows = [...map.values()].sort((a, b) => {
    const pa = a.origen === 'proveedor_inscrito' ? 1 : 0;
    const pb = b.origen === 'proveedor_inscrito' ? 1 : 0;
    return pa - pb;
  });
  setCatalogoVivo(rows);
  return rows;
}

export function catalogoActual() {
  return catalogoVivo || [];
}

export function setCategorias(lista) {
  if (!Array.isArray(lista) || !lista.length) return CATEGORIAS;
  CATEGORIAS.splice(0, CATEGORIAS.length, ...lista);
  return CATEGORIAS;
}

/** El hub-api es la única fuente. El front no mezcla un catálogo local. */
export function mezclarCatalogoVivo(productos) {
  const rows = (productos || []).filter((p) => p && p.sku);
  setCatalogoVivo(rows);
  return rows;
}

export function listarProductos(categoria, sub) {
  let base = catalogoActual();
  if (categoria) base = base.filter((p) => p.categoria === categoria);
  if (sub) base = base.filter((p) => p.sub === sub);
  return base.slice();
}

export function listarDestacados() {
  return catalogoActual().filter((p) => p.destacado);
}

/** El kit no se vende suelto: se arma en el cotizador gratuito. */
export function listarKits() {
  return [];
}

export function buscarProductos({ categoria = '', sub = '', q = '', orden = 'destacado' } = {}) {
  const n = String(q || '').trim().toLowerCase();
  let rows = listarProductos(categoria, sub);
  if (n) {
    rows = rows.filter((p) =>
      [p.nombre, p.marca, p.modelo, p.sku, p.desc, ...(p.specs || [])]
        .join(' ').toLowerCase().includes(n),
    );
  }
  const copia = rows.slice();
  if (orden === 'precio-asc') copia.sort((a, b) => (a.precioRef || 0) - (b.precioRef || 0));
  else if (orden === 'precio-desc') copia.sort((a, b) => (b.precioRef || 0) - (a.precioRef || 0));
  else if (orden === 'nombre') copia.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  else copia.sort((a, b) => Number(!!b.destacado) - Number(!!a.destacado));
  return copia;
}

export function productoPorSku(sku) {
  return catalogoActual().find((p) => p.sku === sku) || null;
}

export const ORIGEN_LABEL = {
  fluxai_hardware: 'Hardware FluxAI',
  alebas: 'Solar Hub',
  proveedor_inscrito: 'Proveedor inscrito',
};

export const fmtCOP = (n) => (n != null ? '$' + Math.round(n).toLocaleString('es-CO') : 'Consultar');

export function flagsDocsProducto(p) {
  const d = p?.docs && typeof p.docs === 'object' ? p.docs : {};
  const f = p?.ficha && typeof p.ficha === 'object' ? p.ficha : {};
  return {
    ficha: !!(d.ficha || f.ficha || f.ficha_nombre),
    manual: !!(d.manual || f.manual || f.manual_nombre),
    cert: !!(d.cert || f.cert || f.cert_nombre),
    cert_tipo: d.cert_tipo || f.cert_tipo || (d.cert || f.cert_nombre ? 'RETIE' : null),
    ficha_nombre: d.ficha_nombre || f.ficha_nombre || null,
    manual_nombre: d.manual_nombre || f.manual_nombre || null,
  };
}

export function urlDocProducto(p, tipo) {
  if (tipo !== 'ficha' && tipo !== 'manual') return null;
  const flags = flagsDocsProducto(p);
  if (!flags[tipo]) return null;
  const id = p?.stock_id || p?.sku;
  if (!id) return null;
  const base = (hubApiBaseUrl() || '/hub-api').replace(/\/+$/, '');
  return `${base}/tienda/doc/${encodeURIComponent(id)}/${tipo}`;
}

export function enunciadoCertProducto(p) {
  const flags = flagsDocsProducto(p);
  if (!flags.cert) return null;
  const tipo = flags.cert_tipo || 'RETIE';
  const quien = String(p?.nombre || `${p?.marca || ''} ${p?.modelo || ''}`).trim() || 'Este producto';
  return `${quien} tiene certificado ${tipo}.`;
}

// Precio preferencial del técnico en la Tienda (compra de un equipo suelto para una obra).
// El cliente final sigue viendo el precio de lista. El % es de lista, no se apila con otro cargo.
export const DESCUENTO_TECNICO_TIENDA_PCT = 15;

/** Precio de lista con el % preferencial del técnico (COP enteros). */
export function precioPreferencialTecnico(precioRef, pct = DESCUENTO_TECNICO_TIENDA_PCT) {
  if (precioRef == null) return null;
  const n = Number(precioRef);
  if (!Number.isFinite(n)) return null;
  const p = Number(pct);
  if (!Number.isFinite(p) || p <= 0) return Math.round(n);
  if (p >= 100) return 0;
  return Math.round(n * (1 - p / 100));
}

/**
 * Precio que ve / paga el comprador.
 * Técnico → preferencial (−DESCUENTO_TECNICO_TIENDA_PCT). Cliente / proveedor / anónimo → lista.
 */
export function lineaPrecioTienda(precioRef, { esTecnico = false, pct = DESCUENTO_TECNICO_TIENDA_PCT } = {}) {
  const lista = precioRef == null ? null : Number(precioRef);
  const listaOk = lista != null && Number.isFinite(lista) ? lista : null;
  const aplica = !!esTecnico && listaOk != null && Number(pct) > 0;
  const unitario = aplica ? precioPreferencialTecnico(listaOk, pct) : listaOk;
  const descuentoPct = aplica ? Number(pct) : 0;
  return {
    lista: listaOk,
    unitario,
    descuentoPct,
    preferencial: aplica,
    ahorro: aplica && listaOk != null && unitario != null ? Math.max(0, listaOk - unitario) : 0,
  };
}

/** Subtotal del carrito al precio que paga el comprador. */
export function totalCarritoTienda(items, opts) {
  return (items || []).reduce((s, x) => {
    const ref = x?.p?.precioRef ?? x?.precioRef;
    const lin = lineaPrecioTienda(ref, opts);
    return s + (lin.unitario || 0) * (Number(x?.qty) || 0);
  }, 0);
}
